import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type {
  DropdownProps,
  DropdownItemProps,
  DropdownHeadingProps,
  DropdownDividerProps,
} from './Dropdown.types';
import { ChevronDownIcon } from './icons';

/**
 * Componente Dropdown del sistema de diseño Siesa
 *
 * Implementación actualizada con React Portal para evitar problemas de overflow (clipping)
 * en contenedores con scroll (como tablas).
 *
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema tipográfico
 */
export const Dropdown: React.FC<DropdownProps> = ({
  variant = 'default',
  children,
  items,
  open: controlledOpen,
  onOpenChange,
  disabled = false,
  className = '',
  menuClassName = '',
  ariaLabel,
  placeholder = 'Options',
  renderTrigger,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [menuCoords, setMenuCoords] = useState<{ top: number; left: number; align: 'left' | 'right' } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Determinar si es controlado o no controlado
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  // Calcular la posición óptima del menú
  const calculateMenuPosition = useCallback(() => {
    if (!dropdownRef.current) return;

    const triggerRect = dropdownRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Estimación básica
    const menuMinWidth = 182; 
    
    // Default: Abajo a la izquierda
    let top = triggerRect.bottom + 4;
    let left = triggerRect.left;
    let align: 'left' | 'right' = 'left';

    // Ajuste horizontal: Si se sale por la derecha, alinear a la derecha del trigger
    if (left + menuMinWidth > viewportWidth) {
      left = triggerRect.right;
      align = 'right';
    }

    // Ajuste vertical básico: Si se sale por abajo, intentar arriba
    // (Nota: Para hacerlo perfecto necesitaríamos la altura real del menú, 
    //  aquí hacemos una estimación o dejamos que el navegador maneje el scroll si es fixed)
    const estimatedHeight = Math.min(items.length * 40 + 20, 300);
    if (top + estimatedHeight > viewportHeight && triggerRect.top > estimatedHeight) {
       top = triggerRect.top - 4;
       // Si abrimos hacia arriba, el CSS transform tendrá que manejar el `bottom-0` simulado
       // O simplemente seteamos `top` restando altura.
       // Para simplificar con Portal y no medir altura pre-render, usaremos abajo por defecto
       // y si el usuario hace scroll, el menú se cierra (comportamiento estándar).
    }

    setMenuCoords({ top, left, align });
  }, [items.length]);

  // Actualizar posición al abrir
  useEffect(() => {
    if (isOpen) {
      calculateMenuPosition();
      // Recalcular al hacer scroll o resize
      window.addEventListener('scroll', calculateMenuPosition, true);
      window.addEventListener('resize', calculateMenuPosition);
    }
    return () => {
      window.removeEventListener('scroll', calculateMenuPosition, true);
      window.removeEventListener('resize', calculateMenuPosition);
    };
  }, [isOpen, calculateMenuPosition]);

  // Función para cambiar el estado
  const handleToggle = () => {
    if (disabled) return;

    const newOpen = !isOpen;
    
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // 1. Verificar si el click fue en el trigger de este dropdown
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      // 2. Verificar si el click fue dentro del menú de este dropdown
      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      // 3. Lógica de anidamiento: Si el click fue dentro de CUALQUIER otro menú de Siesa,
      // no cerramos el actual. Esto permite que selectores de idioma o submenús
      // mantengan abierto el menú padre.
      if (target.closest('[data-siesa-dropdown-menu="true"]')) {
        return;
      }
      
      if (isOpen) {
        if (!isControlled) setInternalOpen(false);
        onOpenChange?.(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, isControlled, onOpenChange]);

  // Handler para item click
  const handleItemClick = (item: DropdownItemProps) => {
    if (item.disabled || item.isDivider || item.isHeading) return;
    if (item.onClick) item.onClick({} as React.MouseEvent<HTMLButtonElement>);
    
    if (!isControlled) setInternalOpen(false);
    onOpenChange?.(false);
  };

  // Renderizar el menú en un Portal
  const renderMenu = () => {
    if (!isOpen || !menuCoords) return null;

    const style: React.CSSProperties = {
      position: 'fixed',
      top: menuCoords.top,
      left: menuCoords.left,
      transform: menuCoords.align === 'right' ? 'translateX(-100%)' : 'none',
      zIndex: 9999, // Asegurar que esté encima de todo (modales, etc.)
      maxHeight: '300px', // Scroll si es muy alto
      overflowY: 'auto',
    };

    // Clases del menú
    const menuClasses = [
      'min-w-[182px]',
      'rounded-lg',
      'p-1',
      'border',
      'bg-background-secondary',
      'border-border-primary',
      'dark:bg-dark-bg-primary',
      'dark:border-dark-border-primary',
      'shadow-lg',
      menuClassName,
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return createPortal(
      <div 
        ref={menuRef} 
        style={style} 
        className={menuClasses}
        data-siesa-dropdown-menu="true"
      >
        {items.map((item, index) => {
          if (item.isDivider) {
            return <DropdownDivider key={index} className={item.className} />;
          }
          if (item.isHeading) {
            return (
              <DropdownHeading key={index} className={item.className}>
                {item.children}
              </DropdownHeading>
            );
          }
          if (item.customContent) {
            return (
              <div key={index} className="w-full">
                {item.customContent}
              </div>
            );
          }
          return (
            <DropdownItem
              key={index}
              {...item}
              onClick={() => handleItemClick(item)}
            />
          );
        })}
      </div>,
      document.body
    );
  };

  // Clases del Trigger
  const triggerClasses = [
    'inline-flex', 'items-center', 'justify-center', 'rounded-lg', 'font-bold', 'text-sm', 'whitespace-nowrap',
    'transition-all', 'duration-150',
    variant === 'icon-only' ? 'w-9 h-9' : 'gap-2 px-3 py-2',
    'bg-white', 'text-content-secondary', 'border', 'border-border-primary', 'hover:bg-background-secondary',
    'dark:bg-dark-bg-primary', 'dark:text-dark-content-primary', 'dark:border-dark-border-primary', 'dark:hover:bg-dark-bg-secondary',
    'focus:outline-hidden', 'focus:ring-2', 'focus:ring-primary-400', 'focus:ring-offset-2',
    'dark:focus:ring-dark-border-custom', 'dark:focus:ring-offset-dark-bg-primary',
    disabled && 'opacity-50', disabled && 'cursor-not-allowed', disabled && 'pointer-events-none',
    className,
  ].filter(Boolean).join(' ').trim();

  // Determinar si el contenedor raíz debe ser full width
  const isFullWidth = className.includes('w-full');

  return (
    <>
      <div 
        ref={dropdownRef} 
        className={`relative ${isFullWidth ? 'w-full flex' : 'inline-flex'}`}
      >
        {renderTrigger ? (
          <div 
            onClick={handleToggle} 
            role="button" 
            tabIndex={disabled ? -1 : 0}
            className={`${isFullWidth ? 'w-full' : 'inline-flex'} items-center justify-center`}
          >
            {renderTrigger(isOpen)}
          </div>
        ) : (
          <button
            type="button"
            className={triggerClasses}
            onClick={handleToggle}
            disabled={disabled}
            aria-label={ariaLabel || (variant === 'icon-only' ? 'Abrir menú' : undefined)}
            aria-haspopup="true"
            aria-expanded={isOpen}
          >
            {variant === 'default' && <span>{children || placeholder}</span>}
            <ChevronDownIcon />
          </button>
        )}
      </div>
      {renderMenu()}
    </>
  );
};

// ... Resto de componentes (DropdownItem, etc.) se mantienen igual ...
// Los copiaré para mantener el archivo completo

export const DropdownItem: React.FC<DropdownItemProps> = ({
  children,
  icon,
  description,
  shortcut,
  disabled = false,
  selected = false,
  onClick,
  className = '',
}) => {
  const itemClasses = [
    'flex', 'items-center', 'gap-3', 'px-4', 'py-2', 'rounded-lg', 'text-sm', 'font-normal', 'w-full', 'text-left',
    'text-content-primary', !disabled && 'hover:bg-bg-primary', selected && 'bg-bg-primary',
    'dark:text-dark-content-primary', !disabled && 'dark:hover:bg-dark-bg-secondary', selected && 'dark:bg-dark-bg-secondary',
    disabled && 'opacity-50', disabled && 'cursor-not-allowed', !disabled && 'cursor-pointer',
    className,
  ].filter(Boolean).join(' ').trim();

  return (
    <button type="button" className={itemClasses} onClick={onClick} disabled={disabled}>
      {icon && (
        <span className="inline-flex items-center justify-center w-4 h-4 text-content-secondary dark:text-dark-content-secondary">
          {icon}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-content-primary dark:text-dark-content-primary">{children}</div>
        {description && (
          <div className="text-xs text-content-secondary dark:text-dark-content-secondary mt-0.5">{description}</div>
        )}
      </div>
      {shortcut && (
        <span className="text-xs text-content-secondary dark:text-dark-content-secondary ml-auto whitespace-nowrap">
          {shortcut}
        </span>
      )}
    </button>
  );
};

export const DropdownHeading: React.FC<DropdownHeadingProps> = ({ children, className = '' }) => {
  const headingClasses = [
    'px-4', 'py-1', 'text-xs', 'font-normal', 'text-content-tertiary', 'dark:text-dark-content-tertiary', className,
  ].filter(Boolean).join(' ').trim();
  return <div className={headingClasses}>{children}</div>;
};

export const DropdownDivider: React.FC<DropdownDividerProps> = ({ className = '' }) => {
  const dividerClasses = ['px-4', 'py-1', className].filter(Boolean).join(' ').trim();
  return (
    <div className={dividerClasses}>
      <div className="h-px bg-border-primary dark:bg-dark-border-primary" />
    </div>
  );
};